import { ArrowUpFromLine, Ban, Landmark, Lock, Users } from 'lucide-react';
import { useEffect, useState, type SyntheticEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerWithdrawal } from '@pf/bff-contract';
import {
  useBalance,
  useCancelWithdrawal,
  useCreateWithdrawal,
  useCustomerWithdrawals,
} from '@/lib/api/bff-queries';
import { BANK_CODES, bankInfo } from '@/lib/banks';
import { formatRelative } from '@/lib/datetime';
import { compareDecimalStrings } from '@/lib/money';
import { cn } from '@/lib/utils';
import { MoneyDisplay } from '@/components/domain/money-display';
import { StateBadge } from '@/components/domain/state-badge';
import { DataTable, type Column } from '@/components/layout/data-table';
import { Field, PageHeader, Section } from '@/components/layout/page';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Callout, EmptyState, ErrorState, Skeleton, TableSkeleton } from '@/components/ui/feedback';
import {
  Code,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/primitives';

/**
 * Withdrawal — request, funds lock, pool position, fulfilment.
 *
 * The screen exists to make one thing legible that a naive cash-out UI hides: **the website locks
 * the money before PaymentFlow ever hears about the request** (SAD §5, D2). So the confirmation
 * leads with the `lock_reference` — the receipt for that lock — rather than with a generic
 * "submitted" toast. That reference is what the withdrawer quotes if anything goes wrong, and it
 * is what every terminal webhook echoes so the website knows which lock to commit or release.
 *
 * Pool position is shown for the same reason: a withdrawal is not "processing", it is queued
 * behind other requests at the same amount, waiting for a depositor to pick that denomination.
 * Saying so turns an opaque wait into an understandable one.
 */
export function WithdrawalPage(): ReactNode {
  const { t } = useTranslation();
  const balance = useBalance();
  const withdrawals = useCustomerWithdrawals();
  const [lastCreated, setLastCreated] = useState<CustomerWithdrawal | null>(null);

  const items = withdrawals.data?.items ?? [];
  const active = items.filter(
    (w) =>
      w.status === 'AVAILABLE' ||
      w.status === 'RESERVED' ||
      w.status === 'LOCKED' ||
      w.status === 'ON_HOLD',
  );
  const history = items.filter((w) => !active.includes(w));

  return (
    <>
      <PageHeader
        title={t('withdrawal.title')}
        description={t('withdrawal.description')}
      />

      <BalanceStrip />

      {lastCreated ? (
        <LockConfirmation
          withdrawal={lastCreated}
          onDismiss={() => {
            setLastCreated(null);
          }}
        />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        <RequestForm
          available={balance.data?.available.value ?? null}
          onCreated={(created) => {
            setLastCreated(created);
          }}
        />

        <div className="space-y-6">
          <Section
            title={t('withdrawal.inPool.sectionTitle')}
            description={t('withdrawal.inPool.sectionDescription')}
          >
            {withdrawals.isLoading ? (
              <TableSkeleton rows={3} columns={4} />
            ) : withdrawals.isError ? (
              <ErrorState
                error={withdrawals.error}
                onRetry={() => {
                  void withdrawals.refetch();
                }}
              />
            ) : (
              <PoolTable
                rows={active}
                empty={
                  <EmptyState
                    icon={<Users className="size-5" aria-hidden="true" />}
                    title={t('withdrawal.inPool.emptyTitle')}
                    description={t('withdrawal.inPool.emptyDescription')}
                  />
                }
              />
            )}
          </Section>

          <Section
            title={t('withdrawal.pastRequests.sectionTitle')}
            description={t('withdrawal.pastRequests.sectionDescription')}
          >
            {withdrawals.isLoading ? (
              <TableSkeleton rows={4} columns={4} />
            ) : (
              <PoolTable
                rows={history}
                empty={
                  <EmptyState
                    title={t('withdrawal.pastRequests.emptyTitle')}
                    description={t('withdrawal.pastRequests.emptyDescription')}
                  />
                }
              />
            )}
          </Section>
        </div>
      </div>
    </>
  );
}

function BalanceStrip(): ReactNode {
  const { t } = useTranslation();
  const balance = useBalance();

  if (balance.isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    );
  }
  if (balance.isError) {
    return (
      <ErrorState
        error={balance.error}
        onRetry={() => {
          void balance.refetch();
        }}
      />
    );
  }
  if (!balance.data) return null;

  const entries = [
    {
      label: t('withdrawal.balance.available.label'),
      amount: balance.data.available,
      hint: t('withdrawal.balance.available.hint'),
      icon: ArrowUpFromLine,
      tone: 'text-foreground',
    },
    {
      label: t('withdrawal.balance.locked.label'),
      amount: balance.data.locked,
      hint: t('withdrawal.balance.locked.hint'),
      icon: Lock,
      tone: 'text-state-flight',
    },
    {
      label: t('withdrawal.balance.pendingCredit.label'),
      amount: balance.data.pending_credit,
      hint: t('withdrawal.balance.pendingCredit.hint'),
      icon: Landmark,
      tone: 'text-state-attention',
    },
  ];

  return (
    <dl className="grid gap-3 sm:grid-cols-3">
      {entries.map(({ label, amount, hint, icon: Icon, tone }) => (
        <div key={label} className="rounded-lg border border-border bg-card p-4">
          <dt className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-muted-foreground">
            <Icon className={cn('size-3.5', tone)} aria-hidden="true" />
            {label}
          </dt>
          <dd className="pt-1.5">
            <MoneyDisplay amount={amount} size="lg" />
          </dd>
          <dd className="pt-0.5 text-2xs text-muted-foreground">{hint}</dd>
        </div>
      ))}
    </dl>
  );
}

function LockConfirmation({
  withdrawal,
  onDismiss,
}: {
  withdrawal: CustomerWithdrawal;
  onDismiss: () => void;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <Card className="border-state-good/40 bg-state-good-bg/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-state-good">
          <Lock className="size-4" aria-hidden="true" />
          {t('withdrawal.lockConfirmation.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid gap-4 sm:grid-cols-3">
          <Field
            label={t('withdrawal.lockConfirmation.lockRefLabel')}
            hint={t('withdrawal.lockConfirmation.lockRefHint')}
          >
            <Code className="text-sm">{withdrawal.lock_reference}</Code>
          </Field>
          <Field label={t('withdrawal.lockConfirmation.amountLockedLabel')}>
            <MoneyDisplay amount={withdrawal.amount} size="lg" />
          </Field>
          <Field
            label={t('withdrawal.lockConfirmation.poolPositionLabel')}
            hint={t('withdrawal.lockConfirmation.poolPositionHint')}
          >
            {withdrawal.pool_position !== null && withdrawal.pool_depth !== null ? (
              <span className="tnum text-sm">
                {t('withdrawal.lockConfirmation.poolPositionValue', {
                  position: withdrawal.pool_position,
                  depth: withdrawal.pool_depth,
                })}
              </span>
            ) : (
              '—'
            )}
          </Field>
        </dl>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {t('withdrawal.lockConfirmation.note')}
        </p>
        <Button variant="outline" size="sm" onClick={onDismiss}>
          {t('withdrawal.lockConfirmation.dismissButton')}
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * `'true'` when this deployment locks payouts to the profile-registered bank account.
 *
 * Read once at module scope: it is a build-time flag (`import.meta.env`), never toggled while the
 * app is running, so every render agrees on it without re-reading `import.meta.env` each time.
 */
const LOCKED_PAYOUT = import.meta.env.VITE_LOCKED_PAYOUT === 'true';

/** A payout account read from the website's `/payout-account` extension endpoint. */
interface LockedAccount {
  bank: string;
  name: string;
  number: string;
}

type LockedPayoutState =
  | { enabled: false }
  | { enabled: true; status: 'loading' }
  | { enabled: true; status: 'ready'; account: LockedAccount }
  | { enabled: true; status: 'error'; serverMessage: string | null };

/**
 * Fetches the profile-registered payout account when {@link LOCKED_PAYOUT} is on.
 *
 * This is a plain `fetch`, not {@link cashierRequest}: `/payout-account` is a website extension
 * outside `@pf/bff-contract`'s manifest, so there is no typed endpoint name to call through the
 * Cashier Profile client. When the flag is off this never fetches at all — default behaviour is
 * untouched.
 */
function useLockedPayoutAccount(): LockedPayoutState {
  const [state, setState] = useState<LockedPayoutState>(
    LOCKED_PAYOUT ? { enabled: true, status: 'loading' } : { enabled: false },
  );

  useEffect(() => {
    if (!LOCKED_PAYOUT) return;
    let cancelled = false;

    void (async () => {
      try {
        const base = import.meta.env.VITE_BFF_BASE ?? '/bff';
        const response = await fetch(`${base}/payout-account`, { credentials: 'same-origin' });
        const payload: unknown = await response.json().catch(() => null);

        if (!response.ok) {
          const serverMessage =
            payload !== null &&
            typeof payload === 'object' &&
            'error' in payload &&
            typeof payload.error === 'object' &&
            payload.error !== null &&
            'message' in payload.error &&
            typeof payload.error.message === 'string'
              ? payload.error.message
              : null;
          if (!cancelled) setState({ enabled: true, status: 'error', serverMessage });
          return;
        }

        const account =
          payload !== null && typeof payload === 'object' && 'account' in payload
            ? (payload.account as Partial<LockedAccount> | null)
            : null;
        if (!account?.bank || !account.name || !account.number) {
          if (!cancelled) setState({ enabled: true, status: 'error', serverMessage: null });
          return;
        }

        if (!cancelled) {
          setState({
            enabled: true,
            status: 'ready',
            account: { bank: account.bank, name: account.name, number: account.number },
          });
        }
      } catch {
        if (!cancelled) setState({ enabled: true, status: 'error', serverMessage: null });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

function RequestForm({
  available,
  onCreated,
}: {
  /** The customer's available balance, or `null` while it is still loading. */
  available: string | null;
  onCreated: (created: CustomerWithdrawal) => void;
}): ReactNode {
  const { t } = useTranslation();
  const create = useCreateWithdrawal();
  const lockedPayout = useLockedPayoutAccount();
  const [amount, setAmount] = useState('1000');
  const [bank, setBank] = useState('KBANK');
  const [accountName, setAccountName] = useState('สุชาติ วัฒนกิจ');
  const [accountNumber, setAccountNumber] = useState('1234567890');

  const normalized = /^\d+(\.\d{1,2})?$/.test(amount) ? amount : null;
  // An unknown balance is not an insufficient one: while the ledger is still loading the form
  // stays usable and says nothing about affordability, rather than accusing the customer of
  // overdrawing an account it has not read yet.
  const overBalance =
    normalized !== null && available !== null && compareDecimalStrings(normalized, available) > 0;

  // In locked-payout mode the receiver account comes from the profile fetch, not the form fields —
  // `null` while that fetch is loading or has failed, which also gates the submit button below.
  const receiverAccount = lockedPayout.enabled
    ? lockedPayout.status === 'ready'
      ? lockedPayout.account
      : null
    : { bank, name: accountName, number: accountNumber };

  const onSubmit = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (normalized === null || overBalance || receiverAccount === null) return;
    create.mutate(
      {
        amount: { value: normalized, currency: 'THB' },
        receiver_account: receiverAccount,
      },
      { onSuccess: onCreated },
    );
  };

  // Wrapped in a `Section` (an `<h2>`) rather than carrying its own card title, so the page's
  // heading levels run h1 → h2 without a gap (WCAG 1.3.1 / axe `heading-order`).
  return (
    <Section
      title={t('withdrawal.form.sectionTitle')}
      description={t('withdrawal.form.sectionDescription')}
      className="h-fit"
    >
      <Card>
        <CardContent className="pt-5">
          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="wd-amount">{t('withdrawal.form.amountLabel')}</Label>
              <Input
                id="wd-amount"
                inputMode="decimal"
                value={amount}
                aria-invalid={normalized === null || overBalance}
                aria-describedby="wd-amount-hint"
                onChange={(event) => {
                  setAmount(event.target.value);
                }}
                className="tnum"
              />
              <p
                id="wd-amount-hint"
                className={cn(
                  'text-2xs',
                  overBalance ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                {normalized === null
                  ? t('withdrawal.form.amountHintInvalid')
                  : overBalance
                    ? t('withdrawal.form.amountHintOverBalance')
                    : t('withdrawal.form.amountHintDefault')}
              </p>
            </div>

            {lockedPayout.enabled ? (
              <LockedAccountFields state={lockedPayout} />
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="wd-bank">{t('withdrawal.form.bankLabel')}</Label>
                  <Select value={bank} onValueChange={setBank}>
                    <SelectTrigger id="wd-bank">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BANK_CODES.map((code) => (
                        <SelectItem key={code} value={code}>
                          {bankInfo(code).name} ({code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="wd-name">{t('withdrawal.form.accountNameLabel')}</Label>
                  <Input
                    id="wd-name"
                    value={accountName}
                    aria-describedby="wd-name-hint"
                    onChange={(event) => {
                      setAccountName(event.target.value);
                    }}
                  />
                  <p id="wd-name-hint" className="text-2xs text-muted-foreground">
                    {t('withdrawal.form.accountNameHint')}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="wd-number">{t('withdrawal.form.accountNumberLabel')}</Label>
                  <Input
                    id="wd-number"
                    inputMode="numeric"
                    className="tnum"
                    value={accountNumber}
                    onChange={(event) => {
                      setAccountNumber(event.target.value.replace(/\D/g, ''));
                    }}
                  />
                </div>
              </>
            )}

            {create.isError ? <ErrorState error={create.error} /> : null}

            <Button
              type="submit"
              className="w-full"
              loading={create.isPending}
              disabled={normalized === null || overBalance || receiverAccount === null}
            >
              <Lock aria-hidden="true" />
              {t('withdrawal.form.submitButton')}
            </Button>

            <Callout tone="info">{t('withdrawal.form.calloutBody')}</Callout>
          </form>
        </CardContent>
      </Card>
    </Section>
  );
}

/**
 * The account section of {@link RequestForm} when {@link LOCKED_PAYOUT} is on: a read-only card
 * instead of the three editable fields, sourced from `/payout-account`.
 */
function LockedAccountFields({
  state,
}: {
  state: Extract<LockedPayoutState, { enabled: true }>;
}): ReactNode {
  const { t } = useTranslation();

  if (state.status === 'loading') {
    return (
      <div className="space-y-1.5">
        <Skeleton className="h-24" />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <Callout tone="danger">
        {state.serverMessage ?? t('withdrawal.form.noPayoutAccount')}
      </Callout>
    );
  }

  const { account } = state;
  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <p className="text-xs font-semibold text-foreground">
        {t('withdrawal.form.lockedAccountTitle')}
      </p>
      <dl className="grid gap-3 sm:grid-cols-3">
        <Field label={t('withdrawal.form.bankLabel')}>
          {bankInfo(account.bank).name} ({account.bank})
        </Field>
        <Field label={t('withdrawal.form.accountNameLabel')}>{account.name}</Field>
        <Field label={t('withdrawal.form.accountNumberLabel')}>{account.number}</Field>
      </dl>
      <p className="text-2xs leading-relaxed text-muted-foreground">
        {t('withdrawal.form.lockedAccountNote')}
      </p>
    </div>
  );
}

function PoolTable({
  rows,
  empty,
}: {
  rows: readonly CustomerWithdrawal[];
  empty: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  const cancel = useCancelWithdrawal();

  const columns: Column<CustomerWithdrawal>[] = [
    {
      id: 'amount',
      header: t('withdrawal.pool.columns.amount'),
      priority: 'primary',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <MoneyDisplay amount={row.amount} size="md" className="font-semibold" />
          <StateBadge machine="withdrawal" status={row.status} size="sm" />
        </div>
      ),
    },
    {
      id: 'account',
      header: t('withdrawal.pool.columns.to'),
      priority: 'secondary',
      cell: (row) => (
        <span className="flex items-center gap-2 text-xs">
          <span
            className="flex size-5 shrink-0 items-center justify-center rounded text-[9px] font-bold text-white"
            style={{ backgroundColor: bankInfo(row.receiver_account.bank).color }}
            aria-hidden="true"
          >
            {bankInfo(row.receiver_account.bank).monogram.slice(0, 1)}
          </span>
          <span className="truncate">{row.receiver_account.bank}</span>
        </span>
      ),
    },
    {
      id: 'position',
      header: t('withdrawal.pool.columns.poolPosition'),
      priority: 'secondary',
      cell: (row) =>
        row.pool_position !== null && row.pool_depth !== null ? (
          <span className="tnum text-xs">
            {t('withdrawal.pool.positionValue', {
              position: row.pool_position,
              depth: row.pool_depth,
            })}
          </span>
        ) : row.matched_transaction_id ? (
          <span className="text-xs text-muted-foreground">{t('withdrawal.pool.matched')}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      id: 'lock',
      header: t('withdrawal.pool.columns.lockReference'),
      priority: 'detail',
      cell: (row) => <Code>{row.lock_reference}</Code>,
    },
    {
      id: 'updated',
      header: t('withdrawal.pool.columns.updated'),
      priority: 'detail',
      cell: (row) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {formatRelative(row.updated_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      srHeader: t('withdrawal.pool.columns.actionsSrHeader'),
      align: 'right',
      priority: 'secondary',
      cell: (row) =>
        row.status === 'AVAILABLE' ? (
          <Button
            variant="ghost"
            size="sm"
            loading={cancel.isPending && cancel.variables === row.id}
            onClick={() => {
              cancel.mutate(row.id);
            }}
          >
            <Ban aria-hidden="true" />
            {t('withdrawal.pool.cancelButton')}
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-3">
      {cancel.isError ? <ErrorState error={cancel.error} /> : null}
      <DataTable
        caption={t('withdrawal.pool.tableCaption')}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        empty={empty}
      />
    </div>
  );
}
